import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Bot, Send, X } from "lucide-react";
import "./Chatbot.css";

const CHAT_PAGES = {
  "/services": ["استكشف خدماتنا", "Explore our services"],
  "/projects": ["شاهد مشاريعنا", "View our projects"],
  "/clients": ["تعرّف على عملائنا", "Meet our clients"],
  "/about": ["تعرّف علينا", "About Applumo"],
  "/careers": ["شاهد الوظائف المتاحة", "View open positions"],
  "/contact": ["تواصل معنا", "Contact us"]
};

function AssistantReply({ content, onNavigate }) {
  const arabic = /[\u0600-\u06ff]/.test(content);
  const pattern = /\[[^\]\n]+\]\((\/(?:services|projects|clients|about|careers|contact))\)|\/(?:services|projects|clients|about|careers|contact)(?![a-zA-Z0-9_/?#-])/g;
  const parts = [];
  let start = 0;
  for (const match of content.matchAll(pattern)) {
    parts.push(content.slice(start, match.index));
    const route = match[1] || match[0];
    parts.push(<Link className="chat-page-button" key={match.index} to={route} onClick={onNavigate}>{CHAT_PAGES[route][arabic ? 0 : 1]}<span aria-hidden="true">{arabic ? "←" : "→"}</span></Link>);
    start = match.index + match[0].length;
  }
  parts.push(content.slice(start));
  return <>{parts}</>;
}

export default function Chatbot() {
  const { i18n } = useTranslation();
  const ar = i18n.language.startsWith("ar");
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const buttonRef = useRef(null);
  const requestRef = useRef(null);
  useEffect(() => () => requestRef.current?.abort(), []);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { if (open) endRef.current?.scrollIntoView?.({ block: "nearest" }); }, [open, messages, busy, error]);
  function close() { setOpen(false); buttonRef.current?.focus(); }
  async function send(event, suggestion) {
    event?.preventDefault();
    const content = (suggestion || input).trim();
    if (!content || busy) return;
    const history = [...messages, { role: "user", content }];
    setMessages(history); setInput(""); setError(""); setBusy(true);
    requestRef.current = new AbortController();
    const timeout = setTimeout(() => requestRef.current?.abort(), 55000);
    try {
      const response = await fetch("/api/chat", {
        method: "POST", headers: { "Content-Type": "application/json" },
        signal: requestRef.current.signal,
        body: JSON.stringify({ messages: history.slice(-11) })
      });
      if (!response.ok) {
        const details = await response.json().catch(() => ({}));
        throw new Error(response.status === 429 ? "limit" : details.error || "unavailable");
      }
      const data = await response.json();
      if (typeof data.reply !== "string") throw new Error("unavailable");
      setMessages([...history, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages(history.slice(0, -1)); setInput(content);
      const replyInArabic = ar || /[\u0600-\u06ff]/.test(content);
      const specificErrors = {
        provider_credits: replyInArabic ? "المساعد متوقف مؤقتاً بسبب رصيد خدمة الذكاء الاصطناعي. تواصل معنا مباشرة." : "The AI service credit limit has been reached. Please contact us directly.",
        provider_auth: replyInArabic ? "خدمة المساعد بحاجة لتحديث إعدادات الاتصال. تواصل معنا مباشرة." : "The assistant's connection settings need updating. Please contact us directly.",
        not_configured: replyInArabic ? "خدمة المساعد غير مهيّأة حالياً." : "The assistant is not configured yet.",
        api_unavailable: replyInArabic ? "سيرفر المساعد غير متاح حالياً. جرّب بعد قليل." : "The assistant server is unavailable. Please try again shortly."
      };
      setError(specificErrors[err.message] || (err.message === "limit"
        ? (replyInArabic ? "طلبات كثيرة، جرّب بعد دقيقة." : "Too many requests. Try again in a minute.")
        : (replyInArabic ? "تعذّر الاتصال. جرّب مجدداً أو تواصل معنا مباشرة." : "Couldn't connect. Please try again or contact us directly.")));
    } finally { clearTimeout(timeout); setBusy(false); }
  }
  const title = ar ? "مساعد Applumo" : "Applumo Assistant";
  return (
    <div className="applumo-chat" dir={ar ? "rtl" : "ltr"} onKeyDown={e => { if (e.key === "Escape" && open) close(); }}>
      {open && <section id="applumo-chat-panel" className="chat-panel" role="dialog" aria-labelledby="chat-title">
        <header className="chat-header">
          <Bot size={26} aria-hidden="true" />
          <div><h2 id="chat-title">{title}</h2><p>{ar ? "إجابات من محتوى موقعنا" : "Answers from our website"}</p></div>
          <button type="button" onClick={close} aria-label={ar ? "إغلاق المحادثة" : "Close chat"}><X size={20} /></button>
        </header>
        <div className="chat-messages" role="log" aria-live="polite" aria-relevant="additions text">
          <div className="chat-bubble assistant">{ar ? "أهلاً! أنا مساعد Applumo الذكي. كيف بقدر أساعدك بخدماتنا، مشاريعنا أو فرص العمل؟" : "Hi! I'm Applumo's AI assistant. How can I help with our services, projects or careers?"}</div>
          {!messages.length && <div className="chat-suggestions">{(ar ? ["شو الخدمات اللي بتقدموها؟", "كيف بتواصل معكم؟", "في فرص عمل؟"] : ["What services do you offer?", "How can I contact you?", "Are you hiring?"]).map(text => <button type="button" key={text} disabled={busy} onClick={() => send(null, text)}>{text}</button>)}</div>}
          {messages.map((m, index) => <div key={index} dir="auto" className={`chat-bubble ${m.role}`}>{m.role === "assistant" ? <AssistantReply content={m.content} onNavigate={close} /> : m.content}</div>)}
          {busy && <div className="chat-bubble assistant" role="status">{ar ? "عم بكتب…" : "Thinking…"}</div>}
          <div ref={endRef} />
        </div>
        {error && <p className="chat-error" role="alert">{error} <a href="/contact">{ar ? "تواصل معنا" : "Contact us"}</a></p>}
        <form className="chat-form" onSubmit={send}>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} maxLength={2000} aria-label={ar ? "رسالتك" : "Your message"} placeholder={ar ? "اسأل عن Applumo…" : "Ask about Applumo…"} />
          <button type="submit" disabled={busy || !input.trim()} aria-label={ar ? "إرسال" : "Send message"}><Send size={19} /></button>
        </form>
        <p className="chat-note">{ar ? "مساعد ذكي • تأكد من التفاصيل المهمة مع فريقنا" : "AI assistant • Confirm important details with our team"}</p>
      </section>}
      <button ref={buttonRef} type="button" className={`chat-launcher${open ? " is-open" : ""}`} aria-label={open ? (ar ? "إغلاق المحادثة" : "Close chat") : (ar ? "افتح المحادثة" : "Open chat")} aria-expanded={open} aria-controls="applumo-chat-panel" onClick={() => open ? close() : setOpen(true)}>
        {open ? <X size={25} aria-hidden="true" /> : <>
          <span className="chat-mascot" aria-hidden="true">
            <span className="chat-mascot-antenna" />
            <span className="chat-mascot-face"><span /><span /></span>
          </span>
          <span className="chat-launcher-caption">{ar ? "مساعدك الذكي" : "Your AI assistant"}</span>
        </>}
      </button>
    </div>
  );
}
